<template>
  <div class="question-details details">
    <div class="close" v-on:click="close()">
      <b-icon icon="close" size="is-large" />
    </div>
    <ul v-if="errorMessages.length > 0" class="error messages">
      <li v-for="(msg, mi) in errorMessages" :key="['error', mi].join('-')">{{msg}}</li>
    </ul>
    <b-select placeholder="Type" v-model="type" required>
      <option
        v-for="typeOpt in types"
        :value="typeOpt.key"
        :key="typeOpt.key"
        type="is-light"
      >{{typeOpt.text}}</option>
    </b-select>
    <b-field label="Text">
      <b-input maxlength="250" rows="3" minlength="3" type="textarea" v-model="text" required></b-input>
    </b-field>
    <b-field label="Unique reference key">
      <b-input maxlength="64" minlength="3" pattern="[a-z0-9_]+" type="key" v-model="key" required></b-input>
    </b-field>
    <b-field label="Active" class="active-group">
      <b-switch v-model="active">{{active|yesNo}}</b-switch>
      <b-button v-if="mayDelete" @click="deleteQuestion" icon-left="delete" rounded>Delete</b-button>
    </b-field>
    <fieldset v-if="showOptions" label="Options" class="options" :class="optionClasses">
      <b-button @click="toggleShowOptionKeys" class="float-right" rounded>{{showOptionkKeysLabel}}</b-button>
      <question-option
        v-for="(opt, oi) in options"
        :key="['question-opt',oi].join('-')"
        :option="opt"
        :index="oi"
        :showKey="showOptionKeys"
        :total="numOptions"
        :other="isOther(opt)"
      ></question-option>
      <b-button @click="addNewOption" icon-left="plus" class="float-right" rounded>Add option</b-button>
    </fieldset>
    <b-field label="Sort order">
      <b-numberinput :max="total" controls-position="compact" controls-rounded v-model="weight"></b-numberinput>
    </b-field>
    <fieldset>
      <legend>Only show this question if another question has a specified value:</legend>
      <b-select placeholder="Show if" v-model="otherQuestion">
        <option
          v-for="qn in otherQuestions"
          :value="qn.key"
          :key="['other', qn._id].join('-')"
          type="is-light"
        >{{qn.text}}</option>
      </b-select>
      <template v-if="hasOtherQuestion">
        <b-select
          v-if="otherQuestionOptions.length > 0"
          placeholder="Has a value of"
          v-model="otherQuestionValue"
        >
          <option
            v-for="(oqo, oqoIndex) in otherQuestionOptions"
            :value="oqo.key"
            :key="['other-option-', oqoIndex].join('-')"
            type="is-light"
          >{{oqo.text}}</option>
        </b-select>
        <b-input v-if="showOtherOptionText" placeholder="Enter value" v-model="otherQuestionValue"></b-input>
      </template>
    </fieldset>
    <b-button type="is-success" class="save" @click="save" rounded>Save</b-button>
    <p v-if="saveMessage.length > 2" class="message">{{saveMessage}}</p>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from "vuex";
import filters from "../mixins/filters";
import { saveQuestion, addQuestion, deleteQuestion } from "../api/methods";
import { toKeyString, emptyString } from "../api/helpers";
import QuestionOption from "./QuestionOption";
import { questionSchema } from "../store/schemas";

export default {
  name: "QuestionDetail",
  components: {
    QuestionOption
  },
  props: {
    question: {
      type: Object,
      default() {
        return questionSchema;
      }
    }
  },
  mixins: [filters],
  mounted() {
    if (this.question instanceof Object) {
      if (this.question.text) {
        this.text = this.question.text;
      }
      if (this.question.type) {
        this.type = this.question.type;
      }
      if (this.question.key) {
        this.key = this.question.key;
      }
      if (this.question.options) {
        if (this.question.options instanceof Array) {
          this.options = this.question.options;
        }
      }
      if (this.question._id === "new") {
        this.options = [];
        this.text = "";
        this.key = "";
        this.initOptions();
      }
      if (this.question.show) {
        if (this.question.show instanceof Object) {
          if (this.question.show.key) {
            this.otherQuestion = this.question.show.key;
            this.otherQuestionValue = this.question.show.value;
          }
        }
      }
      if (this.question.active) {
        this.active = this.question.active;
      }
      if (this.question.weight) {
        this.weight = parseInt(this.question.weight);
      }
    }
  },
  created() {},
  data() {
    return {
      key: "",
      text: "",
      show: {
        key: "",
        value: null
      },
      type: "select_other",
      options: [],
      weight: 0,
      active: false,
      types: [
        { key: "radio", text: "Simlple multiple choice (radio)" },
        { key: "select", text: "Multiple choice (select)" },
        { key: "select_other", text: "Multiple choice + other" },
        { key: "checkbox", text: "Boolean (yes/no)" },
        { key: "text", text: "Text field (single line)" },
        { key: "textarea", text: "Text area (multiple lines)" }
      ],
      otherQuestion: {
        _id: "-",
        text: ""
      },
      otherQuestionValue: null,
      showOptionKeys: false,
      saveMessage: "",
      errorMessages: []
    };
  },
  computed: {
    ...mapGetters(["questions"]),
    showOptions() {
      switch (this.type) {
        case "radio":
        case "select":
        case "select_other":
          return true;
        default:
          return false;
      }
    },
    optionClasses() {
      return [this.showOptionKeys ? "show-keys" : "hide-keys"];
    },
    showOptionkKeysLabel() {
      return this.showOptionKeys ? "Hide option keys" : "Show option keys";
    },
    numOptions() {
      return this.showOptions ? this.options.length : 0;
    },
    otherQuestions() {
      let otherQs = this.$parent.questions.filter(q => {
        let ok = q._id !== this.question._id;
        if (ok) {
          if (q.show instanceof Object) {
            if (q.show.key) {
              ok = q.show.key.length < 2;
            }
          }
        }
        return ok;
      });
      otherQs.unshift({ _id: "-", text: "--- Always show ---", type: "text" });
      return otherQs;
    },
    hasOtherQuestion() {
      return this.otherQuestion.length > 5;
    },
    otherQuestionOptions() {
      let opts = [];
      if (this.hasOtherQuestion) {
        const oq = this.$parent.questions.find(
          q => q.key === this.otherQuestion
        );
        if (oq) {
          switch (oq.type) {
            case "select":
            case "select_other":
            case "radio":
              opts = oq.options;
              break;
            case "checkbox":
              opts = [
                { key: true, text: "true" },
                { key: false, text: "false" }
              ];
              break;
          }
        }
      }
      return opts;
    },
    showOtherOptionText() {
      if (this.hasOtherQuestion) {
        const oq = this.$parent.items.find(q => q.key === this.otherQuestion);
        if (oq) {
          return oq.type === "text";
        }
      }
      return false;
    },
    total() {
      return this.$parent.items.length;
    },
    mayDelete() {
      return this.active === false && this.question._id.length > 5;
    }
  },
  watch: {
    type(cv, pv) {
      if (cv !== pv) {
        this.initOptions();
      }
    },
    text(cv, pv) {
      if (cv.length > 3 && cv !== pv && this.question._id === "new") {
        const keyVal = toKeyString(this.text);
        if (keyVal.length <= 64) {
          this.key = keyVal;
        }
      }
    }
  },
  methods: {
    ...mapMutations(["setQuestions"]),
    close() {
      this.errorMessages = [];
      this.saveMessage = "";
      this.$parent.close();
    },
    toggleShowOptionKeys() {
      this.showOptionKeys = !this.showOptionKeys;
    },
    assignOptionKey(index) {
      if (index < this.options.length) {
        const targetOpt = this.options[index];
        if (targetOpt instanceof Object) {
          let nk = "";
          if (this.isOther(targetOpt)) {
            nk = "other";
          } else {
            const txt = targetOpt.text.trim();
            nk = this.options[index].key = toKeyString(txt);
          }
          this.options[index].key = nk;
        }
      }
    },
    addNewOption() {
      this.addOption(false);
    },
    addOption(alwaysAppend = false, key = "") {
      const newOpt = {
        key: key,
        text: ""
      };
      if (this.type === "select_other" && !alwaysAppend) {
        this.options.splice(this.options.length - 1, 0, newOpt);
      } else {
        this.options.push(newOpt);
      }
    },
    removeOption(index) {
      if (index < this.options.length) {
        this.options.splice(index, 1);
      }
    },
    isOther(opt) {
      if (opt instanceof Object) {
        if (opt.key) {
          return this.type === "select_other" && opt.key === "other";
        }
      }
      return false;
    },
    save() {
      const { _id } = this.question;
      const oldWeight = this.question.weight;
      const isNew = _id.length < 6;
      this.errorMessages = [];
      let newWeight = this.weight;
      if (newWeight > oldWeight) {
        newWeight += 0.5;
      } else if (newWeight < oldWeight) {
        newWeight -= 0.5;
      }
      let data = {
        type: this.type,
        key: this.key,
        text: this.text,
        active: this.active,
        weight: newWeight
      };
      if (this.showOptions) {
        if (this.options instanceof Array) {
          data.options = this.options.map(opt => {
            if (emptyString(opt.key)) {
              opt.key = toKeyString(opt.text);
            }
            return opt;
          });
        } else {
          data.options = [];
        }
      }
      if (this.otherQuestion.length > 3) {
        const oq = this.$parent.items.find(q => q.key === this.otherQuestion);
        if (oq) {
          data.show = {
            key: oq.key,
            value: this.otherQuestionValue
          };
          data.weight = oq.weight + 0.25;
        }
      }
      let valid = true;
      if (this.text.length < 3) {
        valid = false;
        this.errorMessages.push(
          "Please add a question (more than 3 characters)"
        );
      }
      if (this.key.length < 3) {
        valid = false;
        this.errorMessages.push("Please add a unique key");
      } else {
        const oqKeys = this.otherQuestions.map(q => q.key);
        valid = oqKeys.indexOf(data.key) < 0;
        if (!valid) {
          this.errorMessages.push("another question has the same machine key");
        }
      }
      if (this.showOptions && valid) {
        valid = this.options.length >= 2;
        if (!valid) {
          this.errorMessages.push("Please add a unique key");
        }
      }
      if (valid) {
        if (isNew) {
          addQuestion(data).then(this.update);
        } else {
          saveQuestion(_id, data).then(this.update);
        }
      }
    },
    update(data) {
      if (data._id) {
        this.saveMessage =
          this.question._id.length > 5
            ? "Question updated"
            : "New question added";
        setTimeout(() => {
          this.$parent.update();
        }, 500);
        setTimeout(() => {
          this.close();
        }, 1500);
      }
    },
    initOptions() {
      switch (this.type) {
        case "select_other":
        case "select":
        case "radio":
          if (this.options.length < 2) {
            this.addOption(true);
            this.addOption(true);
          }
          if (this.type === "select_other") {
            const otherOpt = this.options.find(op => op.key === "other");
            if (!otherOpt) {
              this.addOption(true, "other");
            }
          }
          break;
      }
    },
    deleteQuestion() {
      if (!this.active) {
        deleteQuestion(this.question._id).then(data => {
          this.$parent.update();
          if (data.question) {
            const { text } = data.question;
            if (text) {
              this.saveMessage = "Question deleted: " + text;
            }
          }
          setTimeout(this.close, 500);
          setTimeout(() => {
            this.saveMessage = "";
          }, 2000);
        });
      }
    }
  }
};
</script>